import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeHeader,
  WireframeContent,
  WireframeTabs,
  WireframeCard,
  WireframeButton,
  WireframeTable,
  StatCard,
  StatusIndicator,
  CrisisBadge,
} from './WireframeLayout';
import {
  Skeleton,
  SkeletonTable,
  LoadingSpinner,
  Modal,
  Select,
  Input,
  useToast,
  EmptyState,
  HelpTooltip,
  RefreshBadge,
} from './WireframeUI';
import { TutorialStartButton } from './WireframeTutorial';
import { Users, CheckCircle, UserPlus, Package, MapPin, ChevronDown, Building, Handshake, Calendar, DollarSign, AlertTriangle, Shield, Layers, Zap } from 'lucide-react';
import { cn } from '../../lib/utils';
import { wireframeMockApi, Team, Resource, regions } from '../../services/wireframeMockApi';
import {
  getResponseData,
  getAllNRCCountries,
  getRegionsForCountry,
  getCountriesWithData,
  ResponseData,
} from '../../data/situationData';
import { nrcLocations } from '../../data/nrcLocations';

// FilterDropdown component
function FilterDropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => options.length > 0 && setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-3 py-1.5 min-w-[120px] cursor-pointer hover:border-gray-300"
      >
        <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[12px] font-semibold flex items-center justify-between gap-2">
          {value}{' '}
          <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform", isOpen && "rotate-180")} />
        </div>
      </div>

      {isOpen && options.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[160px] max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-50",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Comparative advantage icon mapping
const advantageIcons: Record<string, React.ReactNode> = {
  'layers': <Layers className="w-5 h-5" />,
  'users': <Users className="w-5 h-5" />,
  'package': <Package className="w-5 h-5" />,
  'building': <Building className="w-5 h-5" />,
  'droplet': <Package className="w-5 h-5" />,
  'zap': <Zap className="w-5 h-5" />,
  'map-pin': <MapPin className="w-5 h-5" />,
  'heart': <Shield className="w-5 h-5" />,
};

const operations = [
  {
    id: 'cholera-response',
    name: 'Cholera Response - Somali Region',
    severity: 'critical' as const,
    operationId: 'ETH-2025-CHO-001',
    activatedAgo: '2 hours ago',
    teams: 8,
    staff: 42,
    coverage: '3 woredas',
    budget: '$245,000',
    sectors: ['WASH', 'Health', 'Logistics', 'Community Engagement'],
    sectorColors: ['bg-blue-100 text-accent-500', 'bg-green-100 text-green-700', 'bg-amber-100 text-amber-700', 'bg-purple-100 text-purple-700'],
  },
  {
    id: 'flood-prep',
    name: 'Flood Preparedness - Oromia Region',
    severity: 'high' as const,
    operationId: 'ETH-2025-FLD-002',
    activatedAgo: '6 hours ago',
    teams: 4,
    staff: 25,
    coverage: '2 woredas',
    budget: '$120,000',
    sectors: ['Pre-positioning', 'Shelter', 'Early Warning'],
    sectorColors: ['bg-amber-100 text-amber-700', 'bg-blue-100 text-accent-500', 'bg-green-100 text-green-700'],
  },
];

const partners = [
  { org: 'NRC Ethiopia', role: 'Lead Agency', coverage: 'Jijiga, Kebridehar, Gode', contact: 'Melese A. (Program Manager)', status: 'online' as const },
  { org: 'WHO Ethiopia', role: 'Health Coordination', coverage: 'Regional support', contact: 'Dr. Sarah M.', status: 'online' as const },
  { org: 'UNICEF', role: 'WASH Support', coverage: 'Jijiga, Kebridehar', contact: 'Ahmed H.', status: 'online' as const },
  { org: 'WFP', role: 'Logistics', coverage: 'Regional hub', contact: 'Maria L.', status: 'delayed' as const },
  { org: 'Ethiopian Red Cross', role: 'Community Mobilization', coverage: 'Jijiga', contact: 'Dawit T.', status: 'online' as const },
];

export function WireframeOperations() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'operations' }, '*');
    }
  }, [isEmbedded]);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState('Sudan');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [selectedDate, setSelectedDate] = useState('Feb 2026');

  // Date options for the dropdown
  const dateOptions = [
    'Feb 2026',
    'Jan 2026',
    'Dec 2025',
    'Nov 2025',
    'Oct 2025',
    'Last 7 days',
    'Last 30 days',
    'Last 90 days',
  ];

  // Data states
  const [teams, setTeams] = useState<Team[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [stats, setStats] = useState<{ teamsDeployed: number } | null>(null);

  // Loading states
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingResources, setLoadingResources] = useState(true);

  // Last updated timestamps
  const [teamsLastUpdated, setTeamsLastUpdated] = useState<Date | null>(null);
  const [resourcesLastUpdated, setResourcesLastUpdated] = useState<Date | null>(null);

  // Modal states
  const [showDeployTeam, setShowDeployTeam] = useState(false);
  const [showRequestResources, setShowRequestResources] = useState(false);

  const { addToast } = useToast();

  const countriesWithData = getCountriesWithData();
  const allCountries = getAllNRCCountries();
  const regionsList = getRegionsForCountry(selectedCountry);
  const responseData = getResponseData(selectedCountry);
  const hasFullData = countriesWithData.includes(selectedCountry);
  const nrcLocation = nrcLocations.find(loc => loc.country === selectedCountry);

  const tabs = [
    { label: 'Active Operations', active: activeTab === 0 },
    { label: 'Response Strategy', active: activeTab === 1 },
    { label: 'Resources & Budget', active: activeTab === 2 },
    { label: 'Coordination', active: activeTab === 3 },
  ];

  // Load data on mount
  useEffect(() => {
    loadTeams();
    loadResources();
    loadStats();
  }, []);

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');
  };

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const data = await wireframeMockApi.getTeams();
      setTeams(data);
      setTeamsLastUpdated(new Date());
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to load teams' });
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadResources = async () => {
    setLoadingResources(true);
    try {
      const data = await wireframeMockApi.getResources();
      setResources(data);
      setResourcesLastUpdated(new Date());
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to load resources' });
    } finally {
      setLoadingResources(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await wireframeMockApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleTeamDeployed = (team: Team) => {
    setTeams(prev => [...prev, team]);
    setShowDeployTeam(false);
    addToast({ type: 'success', title: 'Team deployed', message: `${team.name} has been deployed to ${team.location}.` });
  };

  const handleResourceRequested = (message: string) => {
    setShowRequestResources(false);
    addToast({ type: 'success', title: 'Resource requested', message });
  };

  return (
    <WireframeLayout embedded={isEmbedded}>
      {!isEmbedded && <WireframeHeader
        title="Operations Center"
        breadcrumbs={[
          { label: 'CLEAR', href: '/wireframes' },
          { label: 'Operations' }
        ]}
        actions={
          <>
            <FilterDropdown
              label="Country"
              value={selectedCountry}
              options={allCountries}
              onChange={handleCountryChange}
            />
            <FilterDropdown
              label="Region"
              value={selectedRegion}
              options={regionsList}
              onChange={setSelectedRegion}
            />
            <FilterDropdown
              label="Date"
              value={selectedDate}
              options={dateOptions}
              onChange={setSelectedDate}
            />
            <TutorialStartButton tutorialId="operations" variant="icon" />
            <WireframeButton icon={<Users className="w-4 h-4" />}>Team Directory</WireframeButton>
            <WireframeButton variant="primary" icon={<CheckCircle className="w-4 h-4" />}>
              Activate Response
            </WireframeButton>
          </>
        }
      />}

      <WireframeContent>
        <WireframeTabs tabs={tabs} onTabClick={setActiveTab} />

        {/* Stats - Enhanced with response data */}
        {hasFullData && responseData ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Active Operations" value="2" valueColor="text-accent-500" />
            <StatCard label="Staff Capacity" value={responseData.nrcCapacity.totalStaff} />
            <StatCard label="Since" value={responseData.nrcCapacity.sinceYear} />
            <StatCard label="Active Programs" value={responseData.nrcCapacity.activePrograms} />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Active Operations" value="2" valueColor="text-accent-500" />
            <StatCard label="Field Teams Deployed" value={stats?.teamsDeployed ?? teams.length} />
            <StatCard label="Staff Mobilized" value="67" />
            <StatCard label="Partner Orgs" value="8" />
          </div>
        )}

        {activeTab === 0 && (
          /* Active Operations */
          <>
            {/* NRC Capacity Card (if data available) */}
            {hasFullData && responseData && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <WireframeCard
                  title="NRC Capacity"
                  subtitle={`${selectedCountry} Operations`}
                >
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 bg-accent-50 border-l-2 border-accent-500">
                      <div className="text-xl font-bold text-accent-600">{responseData.nrcCapacity.totalStaff}</div>
                      <div className="text-[9px] text-gray-500 uppercase">Total Staff</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50">
                      <div className="text-xl font-bold text-gray-900">{responseData.nrcCapacity.nationalStaff}</div>
                      <div className="text-[9px] text-gray-500 uppercase">National</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50">
                      <div className="text-xl font-bold text-gray-900">{responseData.nrcCapacity.internationalStaff}</div>
                      <div className="text-[9px] text-gray-500 uppercase">International</div>
                    </div>
                  </div>
                  <div className="space-y-2 text-[12px]">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Offices</span>
                      <span className="font-medium">{responseData.nrcCapacity.officeCount} locations</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Established</span>
                      <span className="font-medium">Since {responseData.nrcCapacity.sinceYear}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Active Programs</span>
                      <span className="font-medium">{responseData.nrcCapacity.activePrograms}</span>
                    </div>
                  </div>
                </WireframeCard>

                {/* Comparative Advantage */}
                <WireframeCard
                  title="Comparative Advantage"
                  subtitle="NRC strengths"
                  className="lg:col-span-2"
                >
                  <div className="grid grid-cols-2 gap-3">
                    {responseData.comparativeAdvantage.map((adv, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 border-l-2 border-accent-500">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-accent-500">{advantageIcons[adv.icon] || <Layers className="w-4 h-4" />}</span>
                          <span className="font-semibold text-[13px] text-gray-900">{adv.title}</span>
                        </div>
                        <p className="text-[11px] text-gray-600">{adv.description}</p>
                      </div>
                    ))}
                  </div>
                </WireframeCard>
              </div>
            )}

            <WireframeCard
              title="Active Response Operations"
              subtitle="Currently mobilized responses"
              className="mb-6"
              noPadding
            >
              {operations.map((op, index) => (
                <div
                  key={op.id}
                  className={cn('p-5 flex gap-4', index !== operations.length - 1 && 'border-b border-gray-200')}
                >
                  <div
                    className={cn(
                      'w-1 flex-shrink-0',
                      op.severity === 'critical' ? 'bg-red-600' : 'bg-amber-500'
                    )}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{op.name}</span>
                          <CrisisBadge severity={op.severity} />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Activated {op.activatedAgo} • Operation ID: {op.operationId}
                        </div>
                      </div>
                      <WireframeButton
                        size="sm"
                        className={cn(
                          op.severity === 'critical'
                            ? 'bg-red-50 border-red-100 text-red-600'
                            : ''
                        )}
                        onClick={() => addToast({ type: 'info', title: 'Opening operation details', message: `Loading ${op.name}...` })}
                      >
                        View Details
                      </WireframeButton>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-[11px] text-gray-500 uppercase">Teams</div>
                        <div className="font-semibold text-gray-900">{op.teams} deployed</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500 uppercase">Staff</div>
                        <div className="font-semibold text-gray-900">{op.staff} mobilized</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500 uppercase">Coverage</div>
                        <div className="font-semibold text-gray-900">{op.coverage}</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500 uppercase">Budget</div>
                        <div className="font-semibold text-gray-900">{op.budget}</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {op.sectors.map((sector, i) => (
                        <span
                          key={sector}
                          className={cn('text-[11px] px-2 py-1', op.sectorColors[i])}
                        >
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </WireframeCard>

            {/* Two Column: Teams + Resources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Field Teams */}
              <WireframeCard
                title="Field Teams"
                subtitle="Currently deployed"
                headerAction={
                  <WireframeButton
                    size="sm"
                    icon={<UserPlus className="w-4 h-4" />}
                    onClick={() => setShowDeployTeam(true)}
                  >
                    Deploy Team
                  </WireframeButton>
                }
                noPadding
              >
                {loadingTeams ? (
                  <SkeletonTable rows={5} cols={4} />
                ) : teams.length === 0 ? (
                  <EmptyState
                    variant="no-data"
                    title="No teams deployed"
                    message="Deploy a team to start operations."
                    action={{ label: 'Deploy Team', onClick: () => setShowDeployTeam(true) }}
                    className="py-8"
                  />
                ) : (
                  <WireframeTable
                    columns={[
                      { key: 'team', label: 'Team' },
                      { key: 'location', label: 'Location' },
                      { key: 'status', label: 'Status' },
                      { key: 'lastCheckIn', label: 'Last Check-in' },
                    ]}
                    data={teams.slice(0, 5).map((team) => ({
                      team: (
                        <div>
                          <div className="font-medium">{team.name}</div>
                          <div className="text-[11px] text-gray-500">{team.members} members</div>
                        </div>
                      ),
                      location: team.location,
                      status: (
                        <StatusIndicator
                          status={team.status === 'Active' ? 'online' : team.status === 'En Route' ? 'delayed' : 'offline'}
                          label={team.status}
                        />
                      ),
                      lastCheckIn: team.lastCheckIn,
                    }))}
                  />
                )}
              </WireframeCard>

              {/* Resource Status */}
              <WireframeCard
                title="Resource Status"
                subtitle="Critical supplies"
                headerAction={
                  <WireframeButton size="sm" onClick={() => setShowRequestResources(true)}>
                    Request Resources
                  </WireframeButton>
                }
              >
                {loadingResources ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="py-3 border-b border-gray-200 last:border-b-0">
                        <Skeleton className="h-4 w-1/3 mb-2" />
                        <Skeleton className="h-2 w-full" />
                      </div>
                    ))}
                  </div>
                ) : resources.length === 0 ? (
                  <EmptyState
                    variant="no-data"
                    title="No resources tracked"
                    message="Request resources to track status."
                    className="py-8"
                  />
                ) : (
                  <div className="space-y-3">
                    {resources.slice(0, 5).map((resource) => {
                      const percentage = (resource.current / resource.capacity) * 100;
                      const barColor = resource.status === 'adequate' ? 'bg-green-600' :
                                       resource.status === 'low' ? 'bg-amber-600' : 'bg-red-600';
                      const statusColor = resource.status === 'adequate' ? 'text-green-600' :
                                          resource.status === 'low' ? 'text-amber-600' : 'text-red-600';

                      return (
                        <div key={resource.id} className="py-3 border-b border-gray-200 last:border-b-0">
                          <div className="flex justify-between mb-2">
                            <span className="font-medium text-sm">{resource.name}</span>
                            <span className={cn('text-xs capitalize', statusColor)}>{resource.status}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200">
                              <div className={cn('h-full', barColor)} style={{ width: `${percentage}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">
                              {resource.current.toLocaleString()} / {resource.capacity.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </WireframeCard>
            </div>
          </>
        )}

        {activeTab === 1 && (
          /* Response Strategy Tab */
          <>
            {hasFullData && responseData ? (
              <div className="space-y-6">
                {/* Phased Response Strategy */}
                <WireframeCard
                  title="Phased Response Strategy"
                  subtitle="Implementation plan"
                >
                  <div className="space-y-4">
                    {responseData.responsePhases.map((phase, idx) => (
                      <div key={idx} className={cn(
                        'p-4 border-l-4',
                        idx === 0 && 'border-red-500 bg-red-50',
                        idx === 1 && 'border-amber-500 bg-amber-50',
                        idx === 2 && 'border-green-500 bg-green-50',
                      )}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                'text-[10px] font-bold uppercase px-2 py-0.5',
                                idx === 0 && 'bg-red-600 text-white',
                                idx === 1 && 'bg-amber-500 text-white',
                                idx === 2 && 'bg-green-600 text-white',
                              )}>
                                Phase {phase.phase}
                              </span>
                              <span className="font-semibold text-gray-900">{phase.name}</span>
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">{phase.duration}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">${(phase.budget / 1000000).toFixed(1)}M</div>
                            <div className="text-[10px] text-gray-500">Budget</div>
                          </div>
                        </div>
                        <ul className="space-y-1.5">
                          {phase.activities.map((activity, aIdx) => (
                            <li key={aIdx} className="flex items-start gap-2 text-[13px] text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </WireframeCard>

                {/* Implementation Timeline */}
                <WireframeCard
                  title="Implementation Timeline"
                  subtitle="Key milestones"
                >
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                    <div className="space-y-4">
                      {responseData.timeline.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-4 relative">
                          <div className={cn(
                            'w-8 h-8 flex items-center justify-center text-white text-[11px] font-bold z-10',
                            idx === responseData.timeline.length - 1 ? 'bg-green-600' : 'bg-accent-500'
                          )}>
                            D{item.day}
                          </div>
                          <div className="flex-1 pt-1">
                            <div className="font-semibold text-gray-900 text-[13px]">{item.milestone}</div>
                            <div className="text-[12px] text-gray-500">{item.target}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </WireframeCard>

                {/* Partnership Network */}
                <WireframeCard
                  title="Partnership Network"
                  subtitle="Coordination partners"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-3 bg-blue-50 border-l-2 border-blue-500">
                      <div className="text-xl font-bold text-blue-600">{responseData.partnershipNetwork.localNGOs}</div>
                      <div className="text-[10px] text-gray-500 uppercase">Local NGOs</div>
                    </div>
                    <div className="p-3 bg-purple-50 border-l-2 border-purple-500">
                      <div className="text-[13px] font-semibold text-purple-700">{responseData.partnershipNetwork.clusterRole}</div>
                      <div className="text-[10px] text-gray-500 uppercase">Cluster Role</div>
                    </div>
                    <div className="p-3 bg-green-50 border-l-2 border-green-500">
                      <div className="text-[13px] font-semibold text-green-700">{responseData.partnershipNetwork.unAgencies.length} agencies</div>
                      <div className="text-[10px] text-gray-500 uppercase">UN Partners</div>
                    </div>
                    <div className="p-3 bg-amber-50 border-l-2 border-amber-500">
                      <div className="text-[11px] font-semibold text-amber-700">{responseData.partnershipNetwork.communityNetworks}</div>
                      <div className="text-[10px] text-gray-500 uppercase">Community</div>
                    </div>
                  </div>
                </WireframeCard>
              </div>
            ) : (
              <EmptyState
                variant="no-data"
                title="No response strategy data"
                message="Detailed response strategy data is available for Sudan and Ethiopia."
                action={{ label: 'View Sudan Data', onClick: () => handleCountryChange('Sudan') }}
              />
            )}
          </>
        )}

        {activeTab === 2 && (
          /* Resources & Budget Tab */
          <>
            {hasFullData && responseData ? (
              <div className="space-y-6">
                {/* Budget Overview */}
                <WireframeCard
                  title="Resource Requirements"
                  subtitle="6-month budget breakdown"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Total Budget */}
                    <div>
                      <div className="text-center p-4 bg-accent-50 border border-accent-200 mb-4">
                        <div className="text-3xl font-bold text-accent-600">
                          ${(responseData.resourceRequirements.totalBudget / 1000000).toFixed(1)}M
                        </div>
                        <div className="text-[11px] text-gray-500 uppercase">Total Budget (6 months)</div>
                      </div>
                      <div className="space-y-2">
                        {responseData.resourceRequirements.budgetByPhase.map((item, idx) => {
                          const percentage = (item.amount / responseData.resourceRequirements.totalBudget) * 100;
                          return (
                            <div key={idx} className="p-3 bg-gray-50">
                              <div className="flex justify-between mb-2">
                                <span className="text-[13px] font-medium">{item.phase}</span>
                                <span className="text-[13px] font-bold">${(item.amount / 1000000).toFixed(1)}M</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-gray-200">
                                  <div
                                    className={cn(
                                      'h-full',
                                      idx === 0 && 'bg-red-500',
                                      idx === 1 && 'bg-amber-500',
                                      idx === 2 && 'bg-green-500',
                                    )}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-[11px] text-gray-500">{item.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Human Resources */}
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-3">Human Resources</div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="p-3 bg-red-50 border-l-2 border-red-500">
                          <div className="text-xl font-bold text-red-600">{responseData.resourceRequirements.humanResources.surge}</div>
                          <div className="text-[10px] text-gray-500">Surge Staff (Week 1)</div>
                        </div>
                        <div className="p-3 bg-green-50 border-l-2 border-green-500">
                          <div className="text-xl font-bold text-green-600">{responseData.resourceRequirements.humanResources.full}</div>
                          <div className="text-[10px] text-gray-500">Full Staffing</div>
                        </div>
                        <div className="p-3 bg-gray-50">
                          <div className="text-lg font-bold text-gray-900">{responseData.resourceRequirements.humanResources.international}</div>
                          <div className="text-[10px] text-gray-500">International</div>
                        </div>
                        <div className="p-3 bg-gray-50">
                          <div className="text-lg font-bold text-gray-900">{responseData.resourceRequirements.humanResources.national}</div>
                          <div className="text-[10px] text-gray-500">National</div>
                        </div>
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">Specialists Required</div>
                      <div className="flex flex-wrap gap-1.5">
                        {responseData.resourceRequirements.humanResources.specialists.map((spec, idx) => (
                          <span key={idx} className="text-[10px] px-2 py-1 bg-purple-100 text-purple-700">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </WireframeCard>

                {/* Material Resources */}
                <WireframeCard
                  title="Material Resources"
                  subtitle="Equipment and supplies"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {responseData.resourceRequirements.materials.map((material, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 border border-gray-200 flex items-center gap-3">
                        <Package className="w-5 h-5 text-accent-500 flex-shrink-0" />
                        <span className="text-[13px] text-gray-700">{material}</span>
                      </div>
                    ))}
                  </div>
                </WireframeCard>

                {/* Risk Assessment */}
                <WireframeCard
                  title="Risk Assessment"
                  subtitle="Operational risks and mitigation"
                  noPadding
                >
                  <WireframeTable
                    columns={[
                      { key: 'risk', label: 'Risk' },
                      { key: 'severity', label: 'Severity' },
                      { key: 'description', label: 'Description' },
                      { key: 'mitigation', label: 'Mitigation' },
                    ]}
                    data={responseData.riskAssessment.map((item) => ({
                      risk: <strong className="flex items-center gap-2"><AlertTriangle className={cn('w-4 h-4', item.severity === 'high' ? 'text-red-500' : item.severity === 'medium' ? 'text-amber-500' : 'text-green-500')} />{item.risk}</strong>,
                      severity: (
                        <span className={cn(
                          'text-[10px] font-bold uppercase px-2 py-0.5',
                          item.severity === 'high' && 'bg-red-100 text-red-700',
                          item.severity === 'medium' && 'bg-amber-100 text-amber-700',
                          item.severity === 'low' && 'bg-green-100 text-green-700',
                        )}>
                          {item.severity}
                        </span>
                      ),
                      description: <span className="text-[12px]">{item.description}</span>,
                      mitigation: <span className="text-[12px] text-gray-600">{item.mitigation}</span>,
                    }))}
                  />
                </WireframeCard>
              </div>
            ) : (
              <div data-tutorial="resources">
              <WireframeCard
                title={
                  <span className="flex items-center gap-2">
                    Resource Status
                    <HelpTooltip content="Track critical supplies, equipment, and inventory levels across locations" />
                  </span>
                }
                subtitle="Critical supplies and equipment"
                headerAction={
                  <div className="flex items-center gap-2">
                    <RefreshBadge
                      lastUpdated={resourcesLastUpdated}
                      onRefresh={loadResources}
                      isRefreshing={loadingResources}
                    />
                    <div data-tutorial="request-resources">
                    <WireframeButton
                      size="sm"
                      variant="primary"
                      icon={<Package className="w-4 h-4" />}
                      onClick={() => setShowRequestResources(true)}
                    >
                      Request Resources
                    </WireframeButton>
                    </div>
                  </div>
                }
              >
                {loadingResources ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="py-3 border-b border-gray-200 last:border-b-0">
                        <Skeleton className="h-4 w-1/3 mb-2" />
                        <Skeleton className="h-2 w-full" />
                      </div>
                    ))}
                  </div>
                ) : resources.length === 0 ? (
                  <EmptyState
                    variant="no-data"
                    title="No resources tracked"
                    message="Request resources to track their status."
                    action={{ label: 'Request Resources', onClick: () => setShowRequestResources(true) }}
                    className="py-8"
                  />
                ) : (
                  <div className="space-y-3">
                    {resources.map((resource) => {
                      const percentage = (resource.current / resource.capacity) * 100;
                      const barColor = resource.status === 'adequate' ? 'bg-green-600' :
                                       resource.status === 'low' ? 'bg-amber-600' : 'bg-red-600';
                      const statusColor = resource.status === 'adequate' ? 'text-green-600' :
                                          resource.status === 'low' ? 'text-amber-600' : 'text-red-600';

                      return (
                        <div key={resource.id} className="py-3 border-b border-gray-200 last:border-b-0">
                          <div className="flex justify-between mb-2">
                            <div>
                              <span className="font-medium text-sm">{resource.name}</span>
                              <span className="text-xs text-gray-500 ml-2">({resource.location})</span>
                            </div>
                            <span className={cn('text-xs capitalize', statusColor)}>{resource.status}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200">
                              <div className={cn('h-full', barColor)} style={{ width: `${percentage}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">
                              {resource.current.toLocaleString()} / {resource.capacity.toLocaleString()} {resource.unit}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </WireframeCard>
              </div>
            )}
          </>
        )}

        {activeTab === 3 && (
          /* Coordination Tab */
          <>
            {hasFullData && responseData ? (
              <div className="space-y-6">
                {/* Cluster Coordination */}
                <WireframeCard
                  title="Cluster Coordination"
                  subtitle="Sector coordination mechanisms"
                >
                  <div className="space-y-3">
                    {responseData.coordination.clusters.map((cluster, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 border-l-4 border-accent-500">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-semibold text-gray-900">{cluster.name}</div>
                          <span className={cn(
                            'text-[10px] font-bold uppercase px-2 py-0.5',
                            cluster.role === 'Co-lead' && 'bg-accent-500 text-white',
                            cluster.role === 'Member' && 'bg-gray-200 text-gray-700',
                            cluster.role === 'Active member' && 'bg-green-100 text-green-700',
                            cluster.role === 'Observer' && 'bg-amber-100 text-amber-700',
                          )}>
                            {cluster.role}
                          </span>
                        </div>
                        <p className="text-[13px] text-gray-600">{cluster.activities}</p>
                      </div>
                    ))}
                  </div>
                </WireframeCard>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* UN Agencies */}
                  <WireframeCard
                    title="UN Agency Coordination"
                    subtitle="Collaborative relationships"
                  >
                    <div className="space-y-3">
                      {responseData.coordination.unAgencies.map((agency, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 border-l-2 border-blue-500">
                          <div className="w-10 h-10 bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-bold text-[11px]">{agency.name.substring(0, 3).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-[13px] text-gray-900">{agency.name}</div>
                            <div className="text-[12px] text-gray-600">{agency.collaboration}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </WireframeCard>

                  {/* Local Partners */}
                  <WireframeCard
                    title="Local Partners"
                    subtitle="Community engagement"
                  >
                    <div className="space-y-3">
                      {responseData.coordination.localPartners.map((partner, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 border-l-2 border-green-500">
                          <Handshake className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-[13px] text-gray-900">{partner.type}</div>
                            <div className="text-[12px] text-gray-600">{partner.role}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </WireframeCard>
                </div>

                {/* INGOs and Information Sharing */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <WireframeCard
                    title="International NGO Partners"
                    subtitle="Joint operations"
                  >
                    <div className="flex flex-wrap gap-2">
                      {responseData.coordination.ingos.map((ingo, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-[12px] font-medium">
                          {ingo}
                        </span>
                      ))}
                    </div>
                  </WireframeCard>

                  <WireframeCard
                    title="Information Sharing"
                    subtitle="Reporting mechanisms"
                  >
                    <ul className="space-y-2">
                      {responseData.coordination.informationSharing.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-[13px] text-gray-700">
                          <Calendar className="w-4 h-4 text-accent-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </WireframeCard>
                </div>
              </div>
            ) : (
              <WireframeCard
                title="Coordination Partners"
                subtitle="Organizations in the response"
                headerAction={<WireframeButton size="sm">+ Add Partner</WireframeButton>}
                noPadding
              >
                <WireframeTable
                  columns={[
                    { key: 'organization', label: 'Organization' },
                    { key: 'role', label: 'Role' },
                    { key: 'coverage', label: 'Coverage Area' },
                    { key: 'contact', label: 'Contact' },
                    { key: 'status', label: 'Status' },
                  ]}
                  data={partners.map((partner) => ({
                    organization: <strong>{partner.org}</strong>,
                    role: partner.role,
                    coverage: partner.coverage,
                    contact: partner.contact,
                    status: (
                      <StatusIndicator
                        status={partner.status}
                        label={partner.status === 'online' ? 'Active' : 'Standby'}
                      />
                    ),
                  }))}
                />
              </WireframeCard>
            )}
          </>
        )}
      </WireframeContent>

      {/* Deploy Team Modal */}
      <DeployTeamModal
        open={showDeployTeam}
        onClose={() => setShowDeployTeam(false)}
        onDeployed={handleTeamDeployed}
      />

      {/* Request Resources Modal */}
      <RequestResourcesModal
        open={showRequestResources}
        onClose={() => setShowRequestResources(false)}
        onRequested={handleResourceRequested}
        resources={resources}
      />
    </WireframeLayout>
  );
}

// Deploy Team Modal Component
function DeployTeamModal({
  open,
  onClose,
  onDeployed
}: {
  open: boolean;
  onClose: () => void;
  onDeployed: (team: Team) => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'WASH' as Team['type'],
    location: '',
    members: '4',
    operation: 'ETH-2025-CHO-001',
  });
  const [deploying, setDeploying] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async () => {
    if (!formData.name || !formData.location) {
      addToast({ type: 'warning', title: 'Please fill required fields' });
      return;
    }

    setDeploying(true);
    try {
      const team = await wireframeMockApi.deployTeam({
        name: formData.name,
        type: formData.type,
        location: formData.location,
        coordinates: [42.5, 9.2], // Mock coordinates
        members: parseInt(formData.members),
        status: 'En Route',
        operation: formData.operation,
      });
      onDeployed(team);
      // Reset form
      setFormData({
        name: '',
        type: 'WASH',
        location: '',
        members: '4',
        operation: 'ETH-2025-CHO-001',
      });
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to deploy team' });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deploy New Team"
      subtitle="Assign a team to the field operation"
      size="md"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={deploying}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={deploying}
            className="px-4 py-2 text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 flex items-center gap-2"
          >
            {deploying && <LoadingSpinner size="sm" className="text-white" />}
            <MapPin className="w-4 h-4" />
            Deploy Team
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Team Name"
          value={formData.name}
          onChange={(v) => setFormData(prev => ({ ...prev, name: v }))}
          placeholder="e.g., WASH Team Gamma"
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Team Type"
            value={formData.type}
            onChange={(v) => setFormData(prev => ({ ...prev, type: v as Team['type'] }))}
            options={[
              { value: 'WASH', label: 'WASH' },
              { value: 'Health', label: 'Health' },
              { value: 'Protection', label: 'Protection' },
              { value: 'Shelter', label: 'Shelter' },
              { value: 'Food Security', label: 'Food Security' },
              { value: 'Assessment', label: 'Assessment' },
            ]}
          />

          <Input
            label="Team Members"
            value={formData.members}
            onChange={(v) => setFormData(prev => ({ ...prev, members: v }))}
            type="number"
          />
        </div>

        <Input
          label="Deployment Location"
          value={formData.location}
          onChange={(v) => setFormData(prev => ({ ...prev, location: v }))}
          placeholder="e.g., Jijiga, Kebridehar"
          required
        />

        <Select
          label="Assign to Operation"
          value={formData.operation}
          onChange={(v) => setFormData(prev => ({ ...prev, operation: v }))}
          options={[
            { value: 'ETH-2025-CHO-001', label: 'Cholera Response - Somali Region' },
            { value: 'ETH-2025-FLD-002', label: 'Flood Preparedness - Oromia Region' },
          ]}
        />
      </div>
    </Modal>
  );
}

// Request Resources Modal Component
function RequestResourcesModal({
  open,
  onClose,
  onRequested,
  resources
}: {
  open: boolean;
  onClose: () => void;
  onRequested: (message: string) => void;
  resources: Resource[];
}) {
  const [selectedResource, setSelectedResource] = useState('');
  const [quantity, setQuantity] = useState('');
  const [requesting, setRequesting] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async () => {
    if (!selectedResource || !quantity) {
      addToast({ type: 'warning', title: 'Please select a resource and quantity' });
      return;
    }

    setRequesting(true);
    try {
      const result = await wireframeMockApi.requestResource(selectedResource, parseInt(quantity));
      if (result.success) {
        onRequested(result.message);
        setSelectedResource('');
        setQuantity('');
      } else {
        addToast({ type: 'error', title: 'Request failed', message: result.message });
      }
    } catch (error) {
      addToast({ type: 'error', title: 'Failed to request resources' });
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request Resources"
      subtitle="Submit a resource request for the operation"
      size="md"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={requesting}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={requesting}
            className="px-4 py-2 text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 flex items-center gap-2"
          >
            {requesting && <LoadingSpinner size="sm" className="text-white" />}
            <Package className="w-4 h-4" />
            Submit Request
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="Resource Type"
          value={selectedResource}
          onChange={setSelectedResource}
          placeholder="Select a resource..."
          options={resources.map(r => ({ value: r.id, label: `${r.name} (${r.current}/${r.capacity} ${r.unit})` }))}
        />

        <Input
          label="Quantity Needed"
          value={quantity}
          onChange={setQuantity}
          type="number"
          placeholder="Enter quantity..."
        />

        <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
          <strong>Note:</strong> Resource requests are subject to availability and approval.
          Estimated delivery time is 24-48 hours for standard requests.
        </div>
      </div>
    </Modal>
  );
}

export default WireframeOperations;
